class ProductSiblings extends HTMLElement {
  static observedAttributes = ["title", "product-id", "hide-current"];

  constructor() {
    super();
    this.colorUrl = this.dataset.colorUrl;
    console.log('Product Siblings: Started', this.colorUrl);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    console.log(`Attribute ${name} has changed from ${oldValue} to ${newValue}.`);
    switch (name) {
      case "title" || "hide-current":
        if (newValue) {
          this.init(newValue);
        }
        break;
      case "product-id":
        if (/^\d+$/.test(newValue)) {
          this.currentId = +newValue;
        }
        break;
    }
  }

  _initSlider() {
    const track = this.querySelector('.siblings--slider');
    const prev = this.querySelector('.siblings-prev');
    const next = this.querySelector('.siblings-next');
    if (!track || !prev || !next) return;

    const scrollAmount = () => track.clientWidth * 0.75;

    prev.addEventListener('click', () => {
      track.scrollBy({ left: -scrollAmount(), behavior: 'smooth' });
    });

    next.addEventListener('click', () => {
      track.scrollBy({ left: scrollAmount(), behavior: 'smooth' });
    });
  }

  _initTooltips() {
    let tooltip = document.querySelector('.siblings-tooltip');
    if (!tooltip) {
      tooltip = document.createElement('div');
      tooltip.className = 'siblings-tooltip';
      tooltip.style.cssText = `
        position: fixed;
        background: #000;
        color: #fff;
        font-size: 11px;
        padding: 4px 8px;
        border-radius: 4px;
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.15s ease;
        z-index: 9999;
        white-space: nowrap;
      `;
      document.body.appendChild(tooltip);
    }

    this.querySelectorAll('.siblings-swatch[data-colour]').forEach(swatch => {
      swatch.addEventListener('mouseenter', () => {
        tooltip.textContent = swatch.dataset.colour;
        tooltip.style.opacity = '1';
      });
      swatch.addEventListener('mousemove', (e) => {
        tooltip.style.left = e.clientX - (tooltip.offsetWidth / 2) + 'px';
        tooltip.style.top = e.clientY + 14 + 'px';
      });
      swatch.addEventListener('mouseleave', () => {
        tooltip.style.opacity = '0';
      });
    });
  }

  handleize(name) {
    return name
      .toString()
      .normalize('NFKD')
      .replace(/[^\w\s-]/g, '')
      .trim()
      .toLowerCase()
      .replace(/[\s_]+/g, '-')
      .replace(/-+/g, '-');
  }

  sorted(arr = []) {
    if (!Array.isArray(arr)) return arr;
    return arr.sort((a, b) => {
      const nameA = a.swatch.name.toLowerCase();
      const nameB = b.swatch.name.toLowerCase();
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      return 0;
    });
  }

  dataTransform(arr = []) {
    if (!Array.isArray(arr)) return arr;
    let data = [];

    for (const { node } of arr) {
      const { id, options, handle, title } = node;
      const colorOpt = options?.find(({name: n}) => n.toLowerCase() === 'color' || n.toLowerCase() === 'colour');
      if (!colorOpt) continue;

      const { optionValues } = colorOpt;
      for (const value of optionValues) {
        const colorHandle = this.handleize(value.name);
        data.push({
          id,
          handle,
          title,
          swatch: {
            name: value.name,
            color: value?.swatch?.color,
            image: {
              url: value?.swatch?.image?.previewImage?.url || `https:${this.colorUrl.replace('xxxx', colorHandle)}`,
              altText: value?.swatch?.image?.previewImage?.altText
            }
          }
        });
      }
    }

    return this.sorted(data);
  }

  async init(newValue) {
    this.settings = {
      hideCurrent: this.getAttribute('hide-current') === "true" ? true : false
    };

    const query = `{
      products(first: 25, query:"title:${newValue}*") {
        edges {
          node {
            id
            title
            handle
            options(first: 25) {
              name
              optionValues {
                name
                swatch {
                  color
                  image {
                    previewImage {
                      url
                      altText
                    }
                  }
                }
              }
            }
          }
        }
      }
    }`;

    try {
      const req = await fetch(`https://ganactive-com.myshopify.com/api/2025-04/graphql`, {
        method: "POST",
        headers: {
          "Content-Type": "application/graphql",
          "X-Shopify-Storefront-Access-Token": "34744926c84ed526ad80a9b52af4efc5"
        },
        body: query
      });
      const res = await req.json();

      if (req.ok) {
        if (!res.data) {
          console.log('Product Siblings: Missing data. Please try again');
          return;
        }
        const { products: { edges: products } } = res.data;
        const filtered = products.filter(p => p.node.title.startsWith(newValue));
        this.renderProducts(filtered);
      }
    } catch (error) {
      console.log('Error in processing product siblings. Please try again', { error });
    }
  }

  renderProducts(data = null) {
    if (!data) {
      this.handleError(res);
      return;
    }

    const products = this.dataTransform([...data]);
    const useSlider = products.length > 8;

    const swatchHTML = products.map(({ id, handle, swatch, title }) => {
      const pid = id.split('/').at(-1);
      const isCurrentProduct = +pid === this.currentId;
      const swatchName = swatch.name || title;

      const renderSwatch = (swatch, name) => {
        const { color, image } = swatch;
        return `
          <span class="swatch-item${color ? ' has-color' : ''}" style="--aspect-ratio: 1; --bg-color: ${color || ''}">
            ${image && image.url
              ? `<img src="${image.url}" class="theme-img" alt="${image.altText || name}" loading="lazy" width="24" height="24" onerror="this.remove()">`
              : ''
            }
          </span>
        `;
      };

      return `
        <a href="/products/${handle}"
           class="siblings-swatch${isCurrentProduct ? ' active' : ''}${this.settings.hideCurrent && isCurrentProduct ? ' hidden' : ''}"
           data-colour="${swatchName}"
           title="">
          ${swatch ? renderSwatch(swatch, swatchName) : ''}
        </a>
      `;
    }).join('');

    const wrapper = document.createElement('div');
    wrapper.classList.add('siblings-wrapper');

    const prevBtn = `
      <button class="siblings-prev" aria-label="Previous colours">
        <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M7 1L1 7L7 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    `;

    const nextBtn = `
      <button class="siblings-next" aria-label="Next colours">
        <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1 1L7 7L1 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    `;

    if (useSlider) {
      wrapper.innerHTML = `
        ${prevBtn}
        <div class="siblings--slider">
          ${swatchHTML}
        </div>
        ${nextBtn}
      `;
    } else {
      wrapper.innerHTML = `
        <div class="siblings siblings--flex">
          ${swatchHTML}
        </div>
      `;
    }

    const existingSiblingEl = this.querySelector('.siblings-wrapper');
    if (existingSiblingEl) existingSiblingEl.remove();

    this.appendChild(wrapper);
    this.classList.remove('hidden');

    if (useSlider) {
      this._initSlider();
    }

    this._initTooltips();

    const activeEl = this.querySelector('.siblings-swatch.active');
        if (activeEl && activeEl.closest('.siblings--slider')) {
          activeEl.closest('.siblings--slider').scrollLeft = activeEl.offsetLeft;
        }

        this.removeAttribute('title');
      }
}

if (!customElements.get('product-siblings')) {
  customElements.define('product-siblings', ProductSiblings);
}